import { useCallback, useEffect, useMemo, useRef } from 'react';

import { labelRegenerationStatusesApi } from 'api/requests';

/**
 * Poll fallback for the bulk override label phase — the answer to a run that goes SILENT.
 *
 * The socket is the fast path and stays the primary one: per-label events tick the bar and the
 * completion events settle it. But a regeneration that stalls server-side (a poisoned SQS
 * message head-of-line blocking its lane, a consumer death mid-render) stops emitting anything,
 * and before this hook existed the dialog's only way out was its full queued timeout — an
 * operator staring at a frozen bar for the duration. This is the same fallback the lot/NDC
 * review flow already runs (useCommittedLabelRegeneration): ask the LABEL_REGENERATING row tag,
 * id-scoped and cheap, whether anything is still being rebuilt.
 *
 * The all-clear may only settle the phase when something has PROVED the run was real — a poll
 * saw a tagged row, or the socket delivered at least one label — because core's tag-set is
 * best-effort and an untagged run looks exactly like a finished one. Same rule, same reason, as
 * useCommittedLabelRegeneration.
 *
 * One honesty note: the tag expires server-side (~5 minutes) precisely so a dead batch cannot
 * leave rows stuck. An all-clear after a long stall can therefore mean "the tags expired", not
 * "everything rebuilt" — which is why the caller settles with the OBSERVED completed count and
 * reports it, rather than assuming the total.
 */

/* The socket gets this long on its own. A healthy run ticks labels continuously, so a poll
 * before this window would be pure load under a working socket. */
const POLL_START_DELAY_MS = 45000;

/** Gap between polls once the socket has gone quiet. */
const POLL_INTERVAL_MS = 30000;

const REGENERATING = 'REGENERATING';

const useLabelRegenPollFallback = (params) => {
  const { onAllClear, getSocketCompletedCount } = params;

  const delayRef = useRef(null);
  const intervalRef = useRef(null);
  const sawPendingRef = useRef(false);
  const idsRef = useRef([]);
  // Refs, so the interval's closure always sees the current callbacks.
  const onAllClearRef = useRef(onAllClear);
  const getCompletedRef = useRef(getSocketCompletedCount);

  onAllClearRef.current = onAllClear;
  getCompletedRef.current = getSocketCompletedCount;

  const stop = useCallback(() => {
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const statuses = await labelRegenerationStatusesApi(idsRef.current);
      const pendingCount = statuses.filter(
        (row) => row.labelRegenerationStatus === REGENERATING
      ).length;

      if (pendingCount > 0) {
        sawPendingRef.current = true;

        return;
      }

      const isProvenRun = sawPendingRef.current || (getCompletedRef.current?.() ?? 0) > 0;

      // No proof the run is real — an untagged run looks exactly like a finished one, so this
      // stays the timeout's call, not the poll's.
      if (!isProvenRun) return;

      stop();
      onAllClearRef.current?.();
    } catch {
      // A failed poll says nothing about the regeneration. The socket and the timeout are both
      // still live; the next interval asks again.
    }
  }, [stop]);

  const start = useCallback(
    (lineItemIds) => {
      stop();

      idsRef.current = lineItemIds ?? [];
      sawPendingRef.current = false;

      if (!idsRef.current.length) return;

      delayRef.current = setTimeout(() => {
        delayRef.current = null;
        poll();
        intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
      }, POLL_START_DELAY_MS);
    },
    [poll, stop]
  );

  // Never leave a timer behind on unmount.
  useEffect(() => stop, [stop]);

  return useMemo(() => ({ start, stop }), [start, stop]);
};

export default useLabelRegenPollFallback;
